import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Download, Share2, Copy, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function ComingSoonPanel({ event }) {
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [sms, setSms] = useState(false);
    const [busy, setBusy] = useState(false);
    const [joined, setJoined] = useState(false);
    const [copied, setCopied] = useState(false);

    const submit = async (ev) => {
        ev.preventDefault();
        if (!email) return;
        setBusy(true);
        try {
            const { data } = await api.post("/waitlist", {
                event_slug: event.slug, email, name: name || null, phone: phone || null, sms_consent: sms,
            });
            setJoined(true);
            toast.success(`You're on the priority list. Position #${data.position}.`);
        } catch { toast.error("Could not join waitlist"); }
        finally { setBusy(false); }
    };

    const shareUrl = `${window.location.origin}/experiences/${event.slug}`;
    const cardUrl = `${BACKEND_URL}/api/events/${event.slug}/save-the-date.png`;
    const share = async () => {
        try {
            if (navigator.share) {
                await navigator.share({ title: event.title, text: `${event.title} — Save the date. ${event.tagline}`, url: shareUrl });
            } else {
                await navigator.clipboard.writeText(shareUrl);
                setCopied(true); setTimeout(() => setCopied(false), 2000);
                toast.success("Link copied");
            }
        } catch {
            // Share cancelled or copy fell through — non-fatal; a manual copy button is also shown.
        }
    };
    const copyLink = async () => {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true); setTimeout(() => setCopied(false), 2000);
        toast.success("Link copied");
    };

    return (
        <div className="space-y-8" data-testid="coming-soon-panel">
            <div className="border border-[color:var(--espresso)] p-10">
                <p className="eyebrow">Priority waitlist</p>
                <h3 className="font-serif text-3xl mt-3">Be first in the room.</h3>
                <p className="mt-4 text-sm text-[color:var(--cream-muted)]">
                    Subscribers on this list get access to reserve <em>before</em> we open bookings to the public.
                </p>
                <div className="mt-6">
                    <p className="text-xs uppercase tracking-[0.24em] text-[color:var(--gold)]">Scheduled for</p>
                    <p className="font-serif text-xl mt-1">{event.date_confirmed === false ? "Date coming soon" : new Date(event.date_iso).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</p>
                </div>
                {joined ? (
                    <div className="mt-8 border border-[color:var(--gold)] p-6 text-center" data-testid="waitlist-confirmation">
                        <p className="eyebrow">You're in</p>
                        <p className="mt-3 font-serif text-2xl">We'll email you first.</p>
                        <p className="mt-3 text-xs text-[color:var(--cream-muted)]">Keep an eye on your inbox — reservations open to you before the public release.</p>
                    </div>
                ) : (
                    <form onSubmit={submit} className="mt-6 space-y-5">
                        <input className="input-line" required type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="waitlist-email" />
                        <input className="input-line" placeholder="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} data-testid="waitlist-name" />
                        <input className="input-line" placeholder="Mobile (optional)" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="waitlist-phone" />
                        <label className="flex items-start gap-3 text-[0.7rem] text-[color:var(--cream-muted)]">
                            <input type="checkbox" checked={sms} onChange={(e) => setSms(e.target.checked)} className="mt-0.5 accent-[color:var(--gold)]" data-testid="waitlist-sms" />
                            <span>Text me too. Reply STOP to opt out.</span>
                        </label>
                        <button type="submit" disabled={busy} className="btn-gold btn-gold-solid w-full justify-center" data-testid="waitlist-submit">
                            {busy ? "Adding you…" : "Join priority list"}
                        </button>
                        <p className="text-[0.65rem] text-[color:var(--cream-muted)]">Free. No purchase required.</p>
                    </form>
                )}
            </div>

            <div className="border border-[color:var(--gold)]/40 p-6" data-testid="save-the-date-card">
                <p className="eyebrow">Save the date · Share</p>
                <img src={cardUrl} alt={`Save the date — ${event.title}`} className="mt-4 w-full h-auto" data-testid="save-the-date-image" />
                <div className="mt-5 grid grid-cols-3 gap-2">
                    <a href={cardUrl} download={`mrsblackinlove-${event.slug}.png`} className="btn-gold justify-center text-[0.65rem]" data-testid="std-download">
                        <Download size={12} /> Download
                    </a>
                    <button type="button" onClick={copyLink} className="btn-gold justify-center text-[0.65rem]" data-testid="std-copy">
                        {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy link"}
                    </button>
                    <button type="button" onClick={share} className="btn-gold btn-gold-solid justify-center text-[0.65rem]" data-testid="std-share">
                        <Share2 size={12} /> Share
                    </button>
                </div>
                <p className="mt-4 text-[0.65rem] text-[color:var(--cream-muted)]">Save to your camera roll, post to your Story, or send to a friend.</p>
            </div>
        </div>
    );
}

export default function ExperienceDetail() {
    const { slug } = useParams();
    const [e, setE] = useState(null);
    const [tierId, setTierId] = useState("");
    const [qty, setQty] = useState(1);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get(`/events/${slug}`).then((r) => {
            setE(r.data);
            setTierId(r.data.tiers[0]?.id || "");
        });
    }, [slug]);

    if (!e) return <main className="pt-40 min-h-screen text-center text-[color:var(--cream-muted)]">Loading…</main>;

    const isPast = e.date_iso ? new Date(e.date_iso) < new Date() : false;
    const tier = e.tiers.find((t) => t.id === tierId);
    const total = tier ? tier.price_cents * qty : 0;

    const buy = async (ev) => {
        ev.preventDefault();
        if (!name || !email || !tier) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/events/${slug}/order`, {
                event_id: e.id, tier_id: tier.id, quantity: qty,
                name, email, phone: phone || null,
            });
            toast.success("Redirecting to secure checkout…");
            window.location.assign(data.checkout_url);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Unable to start checkout");
        } finally {
            setBusy(false);
        }
    };

    return (
        <main data-testid="experience-detail" className="pt-32 pb-32">
            <section className="relative h-[70vh] overflow-hidden">
                <img src={e.hero_image} alt={e.title} className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 hero-mask" />
                <div className="relative z-10 mx-auto max-w-[1600px] h-full px-6 lg:px-12 flex flex-col justify-end pb-16">
                    <p className="eyebrow">{e.city}{e.date_confirmed === false ? " · Date coming soon" : ` · ${new Date(e.date_iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`}</p>
                    <h1 className="font-display text-6xl md:text-8xl mt-4 max-w-4xl">{e.title}</h1>
                    <p className="mt-4 text-lg text-[color:var(--cream-muted)] max-w-2xl">{e.tagline}</p>
                </div>
            </section>

            <section className="mx-auto max-w-[1600px] px-6 lg:px-12 mt-24 grid lg:grid-cols-12 gap-16">
                <div className="lg:col-span-7">
                    <p className="eyebrow">The evening</p>
                    <p className="mt-4 font-serif text-2xl md:text-3xl leading-relaxed">{e.description}</p>
                    <div className="mt-14">
                        <p className="eyebrow">Highlights</p>
                        <ul className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-4">
                            {e.highlights.map((h) => (
                                <li key={h} className="border-t border-[color:var(--espresso)] pt-4 text-[color:var(--soft-cream)]">{h}</li>
                            ))}
                        </ul>
                    </div>
                    {e.venue && (
                        <div className="mt-14">
                            <p className="eyebrow">Venue</p>
                            <p className="mt-4 text-[color:var(--cream-muted)]">{e.venue}</p>
                        </div>
                    )}
                    {e.policies && (
                        <div className="mt-14">
                            <p className="eyebrow">Policies</p>
                            <p className="mt-4 text-sm text-[color:var(--cream-muted)] max-w-prose">{e.policies}</p>
                        </div>
                    )}
                    {e.gallery && e.gallery.length > 0 && (
                        <div className="mt-16" data-testid="event-gallery">
                            <p className="eyebrow">From the room</p>
                            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {e.gallery.map((g, i) => (
                                    <div key={g} className="zoom-parent overflow-hidden">
                                        <img src={g} alt={`${e.title} moment ${i + 1}`} className="w-full h-[420px] object-cover" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <aside className="lg:col-span-5 lg:sticky lg:top-32 self-start">
                    {isPast ? (
                        <div className="border border-[color:var(--espresso)] p-10 space-y-6" data-testid="concluded-panel">
                            <div>
                                <p className="eyebrow">Concluded</p>
                                <h3 className="font-serif text-3xl mt-3">This evening has passed.</h3>
                            </div>
                            <p className="text-sm text-[color:var(--cream-muted)] leading-relaxed">
                                {e.title} took place on {new Date(e.date_iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}. Tickets are no longer available, and pricing has been retired for this room.
                            </p>
                            <p className="text-sm text-[color:var(--cream-muted)] leading-relaxed">
                                Browse what's next below, or join the priority list for future gatherings — subscribers get first access before the public.
                            </p>
                            <div className="flex flex-wrap gap-3 pt-2">
                                <a href="/experiences" className="btn-gold btn-gold-solid" data-testid="concluded-view-upcoming">View upcoming experiences</a>
                                <a href="/#letters-from-the-table" className="btn-gold" data-testid="concluded-join-list">Join the list</a>
                            </div>
                        </div>
                    ) : e.is_coming_soon ? (
                        <ComingSoonPanel event={e} />
                    ) : (
                        <div className="border border-[color:var(--espresso)] p-10">
                        <>
                            <p className="eyebrow">Reserve</p>
                            <h3 className="font-serif text-3xl mt-3">Choose your seat.</h3>
                            <div className="mt-8 space-y-4">
                                {e.tiers.map((t) => {
                                    const remaining = t.capacity - (t.sold || 0);
                                    const soldOut = remaining <= 0;
                                    return (
                                        <button
                                            key={t.id}
                                            type="button"
                                            disabled={soldOut}
                                            onClick={() => setTierId(t.id)}
                                            data-testid={`tier-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
                                            className={`w-full text-left border p-5 flex flex-col gap-1 transition-colors duration-300 ${
                                                tierId === t.id ? "border-[color:var(--gold)] bg-[color:var(--dark-grey)]" : "border-[color:var(--espresso)] hover:border-[color:var(--gold-soft)]"
                                            } ${soldOut ? "opacity-40 cursor-not-allowed" : ""}`}
                                        >
                                    <div className="flex justify-between items-baseline">
                                        <span className="font-serif text-xl">{t.name}</span>
                                        <span className="text-[color:var(--gold)]">${(t.price_cents / 100).toFixed(0)}</span>
                                    </div>
                                    <p className="text-xs text-[color:var(--cream-muted)]">{t.description}</p>
                                    <p className="text-[0.65rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)] mt-1">
                                        {soldOut ? "Sold out" : `${remaining} of ${t.capacity} remaining`}
                                    </p>
                                </button>
                            );
                        })}
                    </div>
                    <form onSubmit={buy} className="mt-8 space-y-5">
                        <input className="input-line" required placeholder="Full name" value={name} onChange={(ev) => setName(ev.target.value)} data-testid="ticket-name" />
                        <input className="input-line" required type="email" placeholder="Email" value={email} onChange={(ev) => setEmail(ev.target.value)} data-testid="ticket-email" />
                        <input className="input-line" placeholder="Mobile (optional)" value={phone} onChange={(ev) => setPhone(ev.target.value)} data-testid="ticket-phone" />
                        <div className="flex items-center gap-4">
                            <span className="text-xs uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">Quantity</span>
                            <div className="flex items-center gap-3">
                                <button type="button" onClick={() => setQty(Math.max(1, qty - 1))} className="w-8 h-8 border border-[color:var(--espresso)] text-[color:var(--soft-cream)]" data-testid="qty-minus">-</button>
                                <span className="w-6 text-center">{qty}</span>
                                <button type="button" onClick={() => setQty(Math.min(10, qty + 1))} className="w-8 h-8 border border-[color:var(--espresso)] text-[color:var(--soft-cream)]" data-testid="qty-plus">+</button>
                            </div>
                        </div>
                        <div className="flex justify-between items-baseline pt-4 border-t border-[color:var(--espresso)]">
                            <span className="eyebrow">Total</span>
                            <span className="font-serif text-3xl text-[color:var(--gold)]">${(total / 100).toFixed(2)}</span>
                        </div>
                        <button type="submit" disabled={busy || !tier} className="btn-gold btn-gold-solid w-full justify-center" data-testid="checkout-submit">
                            {busy ? "Preparing checkout…" : "Continue to secure checkout"}
                        </button>
                        <p className="text-[0.65rem] text-[color:var(--cream-muted)] leading-relaxed">
                            Secure payment by Square. You'll be redirected to complete purchase. Confirmation email will follow.
                        </p>
                    </form>
                        </>
                        </div>
                    )}
                </aside>
            </section>
        </main>
    );
}
